import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  House,
  Gear,
  UsersThree,
  Briefcase,
  Receipt,
  ClipboardText,
  Microphone,
  HardHat,
  PhoneCall,
  SignOut,
  List,
  X,
  ListChecks,
  CurrencyDollar,
  Sun,
  Moon,
  Desktop,
  Books,
  ChartLineUp,
  Package,
  Sparkle,
  Coins,
  Images,
  Percent,
  BellRinging,
  Wrench,
  CalendarCheck,
  // PR-Cg-Round6 — nav icon for the OWNER-only Overhead tab.
  Buildings,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import ImpersonationBanner from "@/components/ImpersonationBanner";
// PR-Cg-Branding — single source of truth for the wordmark. ``import``
// instead of ``new URL(...)`` so CRA / webpack handles the asset
// pipeline (hashed filename in build/, fallback handling for SSR).
import reformityLogoUrl from "@/assets/reformity-logo.png";

const PRIMARY_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, active: true },
  // PR-Cf — AI Assistant is OWNER-only this iteration (deferred to PR-Cj
  // which will re-open it for REPs with scope-aware tool wrapping).
  { to: "/ai", label: "AI Assistant", icon: Sparkle, active: true, roles: ["OWNER"] },
  // PR-Cf — Leads / Quotes / Invoices stay OWNER + REP. CREW are
  // intentionally excluded so they don't see the lead funnel /
  // ledger lists.
  // PR-Cf.4 — Insights, Expenses, Payroll open to CREW too with
  // scoped views (their own projects' margin; their own payable
  // rows; their own receipts). Backend clamps via Scope.
  { to: "/insights", label: "Insights", icon: ChartLineUp, active: true, roles: ["OWNER", "REP", "CREW", "CREW_LEAD"] },
  { to: "/leads", label: "Leads", icon: ClipboardText, active: true, roles: ["OWNER", "REP"] },
  { to: "/quotes", label: "Quotes", icon: Briefcase, active: true, roles: ["OWNER", "REP"] },
  { to: "/invoices", label: "Invoices", icon: Receipt, active: true, roles: ["OWNER", "REP"] },
  { to: "/projects", label: "Projects", icon: HardHat, active: true },
  // PR-Cg-Phase4 — Standalone schedule calendar. All-roles view;
  // backend RBAC scopes CREW / CREW_LEAD to their own crew's
  // events (including group-inherited via
  // ``visible_project_ids``).
  { to: "/schedule/calendar", label: "Schedule Calendar", icon: CalendarCheck, active: true, crewVisible: true },
  { to: "/expenses", label: "Expenses", icon: CurrencyDollar, active: true, roles: ["OWNER", "REP", "CREW", "CREW_LEAD"] },
  // PR-Cg-Round6 — Overhead / Company Expenses. OWNER-only for the
  // nav entry AND the route (App.js also wraps the element in
  // RoleGate); the page itself renders a "no access" notice for the
  // impersonation edge case. Backend 403s non-OWNER on every
  // /api/overheads/* route.
  { to: "/overhead", label: "Overhead", icon: Buildings, active: true, roles: ["OWNER"] },
  // PR-Cf.4 — Payroll open to CREW + CREW_LEAD with a scoped view
  // (their own payable items only). Bulk-Mark-Paid + Subcontractors
  // block hidden for non-OWNER on the page itself.
  { to: "/payroll", label: "Payroll", icon: Coins, active: true, roles: ["OWNER", "REP", "CREW", "CREW_LEAD"] },
  // PR-Payroll — Rep commissions payroll. Sits as a sub-link under
  // Payroll for visual hierarchy (subtle indent + smaller text via
  // the nav primitive). OWNER + REP only; CREW + CREW_LEAD are
  // route-blind (the route redirects them to /dashboard).
  { to: "/payroll/commissions", label: "Rep Commissions", icon: Percent, active: true, roles: ["OWNER", "REP"], indent: true },
  // PR-Cl Phase 2.5 — /materials promoted to /cost-analysis with
  // a Materials + Labor tab split. The nav entry keeps the same
  // icon and role-set; only the label + URL change.
  { to: "/cost-analysis", label: "Cost & Profit Analysis", icon: Package, active: true, roles: ["OWNER", "REP"] },
  { to: "/today", label: "My Today", icon: ListChecks, active: true, crewVisible: true },
  { to: "/follow-ups", label: "Follow-ups", icon: PhoneCall, active: true, roles: ["OWNER", "REP"] },
  { to: "/meetings", label: "AI Transcription", icon: Microphone, active: true, crewVisible: true },
  // PR-Ck — Media Library. OWNER + REP only. CREW + CREW_LEAD
  // are route-blind (the /library route itself redirects to
  // /dashboard for those roles); the nav link is hidden here
  // for the same effect on the chrome.
  { to: "/library", label: "Library", icon: Images, active: true, roles: ["OWNER", "REP"] },
];

// PR-Cg-SidebarSlim (Phase 1) — Configuration sidebar trimmed to the
// 5 canonical entries. The 7 admin-tool entries previously listed
// here (Rep Attribution Audit, Lead Attribution Audit, Quote Rep
// Backfill, Orphan Lead Restoration, Quote/Invoice Backfill,
// Legacy Notes Migration, Work Types Backfill) are all still
// reachable — either directly by URL, or via the Maintenance
// Console's ``deep_link`` cards (see
// ``/app/backend/api/admin_maintenance_registry.py``). Only the
// sidebar chrome is trimmed here; the routes themselves and the
// pages under ``pages/`` are untouched so the console deep-links
// keep working.
const SECONDARY_NAV = [
  { to: "/settings", label: "Company Settings", icon: Gear, active: true },
  { to: "/settings/team", label: "Team", icon: UsersThree, active: true },
  // PR-Cg-NotifPrefs — Settings → Notifications matrix. OWNER-only;
  // the page itself renders a "no access" notice for non-OWNERs but
  // we also hide the nav entry to keep the secondary list tight.
  {
    to: "/settings/notifications",
    label: "Notifications",
    icon: BellRinging,
    active: true,
    roles: ["OWNER"],
  },
  { to: "/settings/reference-library", label: "Reference Library", icon: Books, active: true },
  // PR-Cg-MaintenanceConsole — OWNER-only unified admin surface.
  // The single entry point for every one-shot admin action. Sits
  // at the bottom of the secondary nav so the operator finds it
  // alongside the older per-action links (which still work — the
  // console is additive, not a hard migration).
  {
    to: "/admin/maintenance-console",
    label: "Maintenance Console",
    icon: Wrench,
    active: true,
    roles: ["OWNER"],
  },
];

// PR-Ce Item 4 — Mobile bottom nav slots. The "More" tap opens the
// existing sidebar drawer (``setMobileOpen(true)``) which already
// renders the full Primary + Secondary nav, so we don't need a
// separate sheet for it. Swapped Invoices → Quotes per the
// operator's quoting-first mobile workflow; Invoices are still
// accessible via More.
//
// PR-Cf — role-aware mobile bottom nav. CREW + CREW_LEAD get
// Home / Today / Meetings / Projects / More (no Quotes — they
// can't see them under PR-Cf scoping anyway). OWNER + REP keep
// the existing Quotes-first slot list.
const MOBILE_BOTTOM_OWNER_REP = [
  { to: "/dashboard", label: "Home", icon: House, active: true },
  { to: "/today", label: "Today", icon: ListChecks, active: true },
  { to: "/quotes", label: "Quotes", icon: Briefcase, active: true },
  { to: "/projects", label: "Projects", icon: HardHat, active: true },
  { drawer: true, label: "More", icon: List, active: true },
];

const MOBILE_BOTTOM_CREW = [
  { to: "/dashboard", label: "Home", icon: House, active: true },
  { to: "/today", label: "Today", icon: ListChecks, active: true },
  { to: "/meetings", label: "Calendar", icon: Microphone, active: true },
  { to: "/projects", label: "Projects", icon: HardHat, active: true },
  { drawer: true, label: "More", icon: List, active: true },
];

function pickMobileBottom(role) {
  if (role === "CREW" || role === "CREW_LEAD") return MOBILE_BOTTOM_CREW;
  return MOBILE_BOTTOM_OWNER_REP;
}

function ThemeToggle({ className = "" }) {
  const { theme, resolvedTheme, cycleTheme } = useTheme();
  const Icon = theme === "system" ? Desktop : resolvedTheme === "dark" ? Moon : Sun;
  const label =
    theme === "light" ? "Light" : theme === "dark" ? "Dark" : "System";
  const next =
    theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
  return (
    <button
      type="button"
      onClick={cycleTheme}
      title={`Theme: ${label} — click for ${next}`}
      aria-label={`Switch theme (current: ${label})`}
      className={[
        "h-9 w-9 grid place-items-center rounded-sm border border-border",
        "text-foreground/80 hover:text-[#F58216] hover:border-[#F58216]/40",
        "bg-transparent transition-colors",
        className,
      ].join(" ")}
      data-testid="theme-toggle-btn"
      data-theme-state={theme}
    >
      <Icon size={16} weight="duotone" />
    </button>
  );
}

function NavItem({ item, onClick, badgeValue }) {
  const Icon = item.icon;
  if (!item.active) {
    return (
      <div
        className="group flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm
                   text-muted-foreground/60 cursor-not-allowed select-none"
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-soon`}
      >
        <Icon size={18} weight="duotone" />
        <span className="flex-1">{item.label}</span>
        <span className="text-[9px] uppercase tracking-widest text-muted-foreground/60 border border-border px-1.5 py-0.5 rounded-sm">
          Soon
        </span>
      </div>
    );
  }
  // Phase F-C1 — render a red dot on items that opt into a badge
  // via ``item.badgeKey``. The parent supplies the count via
  // ``badgeValue``; we only show the dot when the count is > 0.
  const showBadge = Boolean(item.badgeKey) && (badgeValue || 0) > 0;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      end
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium",
          "transition-colors",
          // PR-Payroll — ``indent: true`` lets a nav item hang under
          // its parent as a visual sub-link (smaller font + indented).
          // We don't render a tree control — just a tighter, lighter
          // row so the relationship reads at a glance.
          item.indent ? "ml-3 text-xs py-2" : "",
          isActive
            ? "bg-[rgba(245,130,22,0.08)] text-[#F58216] border-l-2 border-[#F58216] pl-[10px]"
            : "text-foreground/80 hover:text-foreground hover:bg-secondary",
        ].join(" ")
      }
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={18} weight="duotone" />
      <span className="flex-1">{item.label}</span>
      {showBadge && (
        <span
          className="relative inline-flex h-2.5 w-2.5"
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-badge`}
          title={`${badgeValue} item${badgeValue === 1 ? "" : "s"} to review`}
        >
          <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-50 animate-ping" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
        </span>
      )}
    </NavLink>
  );
}

function SidebarContent({ user, onLogout, onNavigate, badges }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          {/* PR-Cg-Branding — sidebar wordmark. Wrapped in a
              white card because the Reformity mark has dark ink
              which wouldn't read on the dark sidebar (the rest of
              the dashboard uses dark surfaces for chrome; the
              brand panel itself stays light so the lockup pops).
              40px effective height keeps the prior placeholder
              footprint so adjacent spacing/nav grid is undisturbed.
              ``dark:bg-white`` ensures both themes render the
              light card — the symbol-only PWA icon (transparent
              bg) lives separately in the favicon. */}
          <div className="bg-white rounded-md px-2 py-1.5 shadow-sm">
            <img
              src={reformityLogoUrl}
              alt="Reformity Inc."
              className="h-7 w-auto object-contain shrink-0 block"
              data-testid="sidebar-brand-logo"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="ref-overline text-muted-foreground">CRM</div>
          </div>
          <ThemeToggle />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
        <div>
          <div className="px-3 ref-overline mb-2">Workspace</div>
          <nav className="flex flex-col gap-0.5">
            {PRIMARY_NAV.filter((item) => {
              // Items with an explicit `roles` allow-list only show for
              // those roles. Items without `roles` show for everyone (the
              // legacy behaviour predating Phase 6A).
              if (!item.roles) return true;
              return item.roles.includes(user?.role);
            }).map((item) => (
              <NavItem
                key={item.to}
                item={item}
                onClick={onNavigate}
                badgeValue={item.badgeKey ? badges?.[item.badgeKey] : 0}
              />
            ))}
          </nav>
        </div>

        <div>
          <div className="px-3 ref-overline mb-2">Configuration</div>
          <nav className="flex flex-col gap-0.5">
            {SECONDARY_NAV.filter((item) => {
              if (!item.roles) return true;
              return item.roles.includes(user?.role);
            }).map((item) => (
              <NavItem
                key={item.to}
                item={item}
                onClick={onNavigate}
                badgeValue={item.badgeKey ? badges?.[item.badgeKey] : 0}
              />
            ))}
          </nav>
        </div>
      </div>

      <div className="border-t border-border p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-sm bg-secondary border border-border grid place-items-center">
            <span className="font-display font-bold text-sm">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={user?.role === "OWNER" ? "ref-badge-owner" : "ref-badge"}>
                {user?.role}
              </span>
            </div>
          </div>
        </div>
        {/* PR-Cf — OWNER-only impersonation dropdown. Renders inside
            the sidebar footer so it shares the avatar column real
            estate. Non-OWNERs get nothing. */}
        <div className="mb-3">
          <ImpersonationBanner mode="dropdown" />
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="w-full ref-btn-ghost"
          data-testid="logout-btn"
        >
          <SignOut size={14} weight="bold" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Phase F-C1 — sidebar-badge state. Currently a single key
  // (``rep_attribution_gaps``) but the shape is generic so future
  // admin gates (e.g. "expired quotes", "failed receipts") can opt
  // in without another fetch loop.
  const [badges, setBadges] = useState({});

  React.useEffect(() => {
    // Only OWNER sees the audit nav link, so only OWNER pays the
    // count fetch. Skip for everyone else.
    if (user?.role !== "OWNER") return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const [repRes, leadRes] = await Promise.all([
          api.get("/admin/rep-attribution/gaps?limit=0").catch(() => null),
          // PR-Bugfix-2 Bug 2 — Lead Attribution Audit badge. Same
          // 5-min refresh cadence as the rep audit.
          api.get("/leads/_audit/unassigned-count").catch(() => null),
        ]);
        if (cancelled) return;
        if (repRes?.data) {
          const total =
            (repRes.data.total_quotes_missing_rep || 0) +
            (repRes.data.total_invoices_missing_rep || 0);
          setBadges((prev) => ({ ...prev, rep_attribution_gaps: total }));
        }
        if (leadRes?.data) {
          setBadges((prev) => ({
            ...prev,
            unassigned_leads: leadRes.data.unassigned_count || 0,
          }));
        }
      } catch {
        // Soft-fail — leave the badge unchanged on transient error.
      }
    };
    fetchBadges();
    // Refresh every 5 minutes — light enough to be invisible,
    // frequent enough that the operator sees gaps drop as they
    // attribute reps via the audit page.
    const id = setInterval(fetchBadges, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card flex-col"
        data-testid="sidebar-desktop"
      >
        <SidebarContent user={user} onLogout={handleLogout} badges={badges} />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="overlay"
              className="fixed inset-0 bg-black/70 z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              key="drawer"
              className="fixed top-0 left-0 bottom-0 w-72 bg-card border-r border-border z-50 lg:hidden"
              initial={{ x: -288 }}
              animate={{ x: 0 }}
              exit={{ x: -288 }}
              transition={{ type: "tween", duration: 0.25 }}
              data-testid="sidebar-mobile-drawer"
            >
              <SidebarContent
                user={user}
                onLogout={() => {
                  setMobileOpen(false);
                  handleLogout();
                }}
                onNavigate={() => setMobileOpen(false)}
                badges={badges}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* PR-Cf — active impersonation banner. Renders only when an
            impersonation session is active AND the caller is OWNER.
            Sticky-top so it stays visible while scrolling. */}
        <ImpersonationBanner mode="banner" />
        {/* Mobile top bar */}
        <header
          className="lg:hidden flex items-center justify-between px-4 h-14 border-b border-border bg-card sticky top-0 z-30"
          data-testid="topbar-mobile"
        >
          <button
            type="button"
            className="p-2 -ml-2 text-foreground"
            onClick={() => setMobileOpen(true)}
            data-testid="menu-toggle-btn"
            aria-label="Open menu"
          >
            {mobileOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
          </button>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm bg-[#F58216] grid place-items-center">
              <span className="font-display font-black text-black text-sm">R</span>
            </div>
            <span className="font-display font-bold">Reformity</span>
          </div>
          <ThemeToggle />
        </header>

        <main className="flex-1 min-w-0 pb-32 lg:pb-0" data-testid="main-content">
          <Outlet />
        </main>

        {/* Mobile bottom nav. Sits ABOVE the Emergent badge (which is fixed
            bottom-right at z-9999, ~178×40px) so all 5 items remain tappable.
            On mobile we leave a 64px strip below the nav for the badge to live in. */}
        <nav
          className="lg:hidden fixed bottom-16 left-0 right-0 h-16 bg-card border-t border-b border-border flex z-30 shadow-lg shadow-black/40"
          data-testid="bottom-nav"
        >
          {MOBILE_BOTTOM_OWNER_REP && pickMobileBottom(user?.role).map((item) => {
            const Icon = item.icon;
            if (item.drawer) {
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => setMobileOpen(true)}
                  className="flex-1 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground transition-colors"
                  data-testid={`bottomnav-${item.label.toLowerCase()}`}
                >
                  <Icon size={20} weight="duotone" />
                  <span>{item.label}</span>
                </button>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end
                className={({ isActive }) =>
                  [
                    "flex-1 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-widest font-bold transition-colors",
                    isActive ? "text-[#F58216]" : "text-muted-foreground hover:text-foreground",
                  ].join(" ")
                }
                data-testid={`bottomnav-${item.label.toLowerCase()}`}
              >
                <Icon size={20} weight="duotone" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
